import { api, queryKeys } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useLogout, useSession } from '../hooks/useSession';
import { formatLds, formatUsd } from '../lib/format';
import { userNav } from '../nav';
import {
  Button,
  PanelLayout,
  Sidebar,
  SidebarLogo,
  SidebarUser,
  SpinnerBlock,
  Topbar,
  TopbarBalance,
} from '../ui';
import { ImpersonationBanner } from './ImpersonationBanner';
import { Nav } from './Nav';

/** Topbar balance pills — activation (gold ★) and income (green) per §6.2. */
function BalancePills() {
  const { data } = useQuery({
    queryKey: queryKeys.wallet(),
    queryFn: () => api.wallet.balances().then((r) => r.data),
    staleTime: 15_000,
  });

  return (
    <>
      <TopbarBalance
        tone="gold"
        icon="★"
        label="Activation"
        value={formatUsd(data?.activation_balance)}
      />
      <TopbarBalance tone="income" icon="▶" label="Income" value={formatLds(data?.income_balance_lds)} />
    </>
  );
}

export function AppShell() {
  const { isLoading, isAuthenticated, session, user, isImpersonating } = useSession();
  const logout = useLogout();
  const location = useLocation();

  if (isLoading) return <SpinnerBlock />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <PanelLayout
      sidebar={
        <Sidebar
          logo={<SidebarLogo title={'Alpha One\nWeb3 Ecosystem'} />}
          user={user ? <SidebarUser name={user.name} userId={user.user_code} /> : undefined}
        >
          <Nav groups={userNav} />
        </Sidebar>
      }
      topbar={
        <Topbar
          actions={
            <>
              <BalancePills />
              <Button
                variant="outline-gold"
                size="sm"
                leftIcon={<LogOut size={16} />}
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
              >
                Logout
              </Button>
            </>
          }
        />
      }
      impersonationBanner={
        isImpersonating && user ? (
          <ImpersonationBanner
            userName={user.name}
            impersonatorName={session?.impersonator_name ?? null}
          />
        ) : undefined
      }
    >
      <Outlet />
    </PanelLayout>
  );
}
